/**
 * Demo2-only: reconstruct a chart-ready FinancialHealthStats[] from
 * algo_run_evidence when /lender/members/:id/history returns a degenerate
 * (single current/restated month) series.
 *
 * Scope: branch `demo2` only. Display-only synthesis for trend charts;
 * does not change scoring. The canonical latest-month row from /history is
 * overlaid by the caller via overlayLatestMonth() to preserve
 * official_current / restated semantics.
 *
 * Fail-soft: returns [] on any error or empty evidence so the caller can
 * fall through to its existing single-row behavior.
 */

import { getRunEvidence } from '../services/auditEvidenceService';
import { isDemo2Member } from '../services/demo2PersonaOverlay';
import type { FinancialHealthStats } from '../types/financialStats';

const FACTOR_WEIGHTS = {
  factor_dti: 0.2,
  factor_cash_on_hand: 0.125,
  factor_spending: 0.25,
  factor_savings: 0.15,
  factor_debt: 0.175,
  factor_payment_history: 0.1,
} as const;

type FactorStatKey = keyof typeof FACTOR_WEIGHTS;

const FACTOR_ENTITY_TO_STAT_KEY: Record<string, FactorStatKey> = {
  debt_to_income_factor: 'factor_dti',
  liquid_reserves_factor: 'factor_cash_on_hand',
  cash_flow_factor: 'factor_spending',
  savings_factor: 'factor_savings',
  debt_factor: 'factor_debt',
  debt_management_factor: 'factor_debt',
  payment_history_factor: 'factor_payment_history',
};

interface MonthBucket {
  factor_dti: number | null;
  factor_cash_on_hand: number | null;
  factor_spending: number | null;
  factor_savings: number | null;
  factor_debt: number | null;
  factor_payment_history: number | null;
  dti_ratio: number | null;
  number_of_late_payments: number | null;
  gross_income: number | null;
  monthly_expenses: number | null;
  money_leftover: number | null;
  net_savings: number | null;
  liquid_reserves: number | null;
  minimum_debt_payments: number | null;
  total_outstanding_adverse_debt: number | null;
}

function emptyBucket(): MonthBucket {
  return {
    factor_dti: null,
    factor_cash_on_hand: null,
    factor_spending: null,
    factor_savings: null,
    factor_debt: null,
    factor_payment_history: null,
    dti_ratio: null,
    number_of_late_payments: null,
    gross_income: null,
    monthly_expenses: null,
    money_leftover: null,
    net_savings: null,
    liquid_reserves: null,
    minimum_debt_payments: null,
    total_outstanding_adverse_debt: null,
  };
}

function firstNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = source[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

function preferFirst(current: number | null, next: number | null): number | null {
  return current != null ? current : next;
}

// Phase 14: month-key arithmetic for the Demo2 date-window shift.
// Operates on YYYY-MM strings; also accepts YYYY-MM-DD by truncation.
// All helpers are pure and string-safe — no Date timezone drift.
export function parseMonthKey(
  key: string | null | undefined
): { year: number; month: number } | null {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})/.exec(key);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  if (month < 1 || month > 12) return null;
  return { year, month };
}

export function shiftMonthKey(key: string, offsetMonths: number): string {
  if (!offsetMonths) return key;
  const p = parseMonthKey(key);
  if (!p) return key;
  const total = p.year * 12 + (p.month - 1) + offsetMonths;
  const ny = Math.floor(total / 12);
  const nm = (total % 12 + 12) % 12 + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

// Forward-only offset: returns months>=0 between maxMonth and anchorMonth.
// Returns 0 when anchor is missing, evidence already current, or evidence ahead.
export function computeMonthOffset(
  anchorMonth: string | null | undefined,
  maxMonth: string | null | undefined
): number {
  const a = parseMonthKey(anchorMonth ?? null);
  const m = parseMonthKey(maxMonth ?? null);
  if (!a || !m) return 0;
  const diff = (a.year - m.year) * 12 + (a.month - m.month);
  return diff > 0 ? diff : 0;
}

// Phase 14: compute the demo2 shift only for known Demo2 members and only
// when the anchor (typically lender_member_summary.score_month surfaced via
// /history) is ahead of the latest evidence month_key.
export function computeDemo2ShiftOffset(
  memberId: string | null | undefined,
  anchorMonth: string | null | undefined,
  maxEvidenceMonth: string | null | undefined
): number {
  if (!isDemo2Member(memberId)) return 0;
  return computeMonthOffset(anchorMonth, maxEvidenceMonth);
}

function computeComposite(b: MonthBucket): number | null {
  let sum = 0;
  let weight = 0;
  for (const key of Object.keys(FACTOR_WEIGHTS) as FactorStatKey[]) {
    const v = b[key];
    if (typeof v === 'number') {
      sum += v * FACTOR_WEIGHTS[key];
      weight += FACTOR_WEIGHTS[key];
    }
  }
  if (weight === 0) return null;
  return Math.round(sum / weight);
}

// Phase 14: optional Demo2 month-shift parameters. When `memberId` is a
// Demo2 persona and `anchorMonth` is ahead of the evidence's max month_key,
// the returned series labels are shifted forward by that offset so the chart
// window ends at the current Demo2 demo month. Values are untouched.
export interface BuildFallbackOptions {
  anchorMonth?: string | null;
  memberId?: string | null;
}

export async function buildFallbackHistoryFromEvidence(
  runId: string,
  options: BuildFallbackOptions = {}
): Promise<FinancialHealthStats[]> {
  try {
    const response = await getRunEvidence(runId);
    if (!response.evidence || response.evidence.length === 0) return [];

    const byMonth = new Map<string, MonthBucket>();
    for (const rec of response.evidence) {
      const month = rec.month_key;
      if (!month) continue;
      const bucket = byMonth.get(month) ?? emptyBucket();
      const ims = (rec.intermediate_metrics ?? {}) as Record<string, unknown>;

      const factorStatKey = FACTOR_ENTITY_TO_STAT_KEY[rec.entity_name];
      if (factorStatKey) {
        const score = ims.score;
        if (typeof score === 'number') bucket[factorStatKey] = score;
      }

      // Factor-derived intermediate metrics (used as fallback when METRIC entities are absent)
      if (rec.entity_name === 'debt_to_income_factor') {
        bucket.dti_ratio = preferFirst(bucket.dti_ratio, firstNumber(ims, ['dti_ratio']));
        bucket.minimum_debt_payments = preferFirst(
          bucket.minimum_debt_payments,
          firstNumber(ims, ['minimum_payments_all_debt'])
        );
        const yearly = firstNumber(ims, ['yearly_gross_income']);
        if (yearly != null) {
          bucket.gross_income = preferFirst(bucket.gross_income, yearly / 12);
        }
      }
      if (rec.entity_name === 'payment_history_factor') {
        bucket.number_of_late_payments = preferFirst(
          bucket.number_of_late_payments,
          firstNumber(ims, ['number_of_late_payments', 'late_payments'])
        );
      }
      if (rec.entity_name === 'cash_flow_factor') {
        bucket.gross_income = preferFirst(bucket.gross_income, firstNumber(ims, ['money_in']));
        bucket.monthly_expenses = preferFirst(bucket.monthly_expenses, firstNumber(ims, ['money_out']));
        bucket.money_leftover = preferFirst(
          bucket.money_leftover,
          firstNumber(ims, ['money_left_over', 'net_cash_flow'])
        );
      }
      if (rec.entity_name === 'liquid_reserves_factor') {
        bucket.liquid_reserves = preferFirst(bucket.liquid_reserves, firstNumber(ims, ['liquid_balance']));
      }
      if (rec.entity_name === 'savings_factor') {
        bucket.net_savings = preferFirst(bucket.net_savings, firstNumber(ims, ['net_savings']));
      }

      // METRIC entities (preferred sources; override factor-derived values)
      if (rec.entity_name === 'money_in_1m') {
        const v = firstNumber(ims, ['sum_deposits', 'money_in', 'total']);
        if (v != null) bucket.gross_income = v;
      }
      if (rec.entity_name === 'money_out_1m') {
        const v = firstNumber(ims, ['sum_withdrawals', 'money_out', 'total']);
        if (v != null) bucket.monthly_expenses = v;
      }
      if (rec.entity_name === 'money_leftover_1m') {
        const v = firstNumber(ims, ['money_left_over', 'money_leftover', 'net_flow']);
        if (v != null) bucket.money_leftover = v;
      }
      if (rec.entity_name === 'net_savings_1m') {
        const v = firstNumber(ims, ['net_savings', 'savings', 'total']);
        if (v != null) bucket.net_savings = v;
      }
      if (rec.entity_name === 'cash_on_hand') {
        const v = firstNumber(ims, ['total_cash_on_hand', 'total_balance', 'cash_on_hand', 'balance']);
        if (v != null) bucket.liquid_reserves = v;
      }
      if (rec.entity_name === 'dti_ratio') {
        const v = firstNumber(ims, ['dti_ratio', 'dti', 'ratio']);
        if (v != null) bucket.dti_ratio = v;
      }
      if (rec.entity_name === 'monthly_debt_payments') {
        const v = firstNumber(ims, [
          'minimum_payments_all_debt',
          'total_actual_debt_payments',
          'total_loan_payments',
        ]);
        if (v != null) bucket.minimum_debt_payments = v;
      }
      if (rec.entity_name === 'total_debt') {
        const direct = firstNumber(ims, ['total_debt', 'outstanding_debt']);
        if (direct != null) {
          bucket.total_outstanding_adverse_debt = direct;
        } else {
          const shortTerm = firstNumber(ims, ['short_term_debt_balance']);
          const loan = firstNumber(ims, ['loan_balance']);
          if (shortTerm != null || loan != null) {
            bucket.total_outstanding_adverse_debt = (shortTerm ?? 0) + (loan ?? 0);
          }
        }
      }
      if (rec.entity_name === 'late_payments') {
        const v = firstNumber(ims, ['number_of_late_payments', 'late_payments', 'late_payment_count', 'count']);
        if (v != null) bucket.number_of_late_payments = v;
      }

      byMonth.set(month, bucket);
    }

    const months = Array.from(byMonth.keys()).sort();
    // Phase 14: Demo2-only forward shift. Anchor = current Demo2 score month
    // (typically /history latest). Offset is non-zero only when caller is a
    // Demo2 persona and evidence trails the anchor.
    const maxMonth = months.length > 0 ? months[months.length - 1] : null;
    const shiftOffset = computeDemo2ShiftOffset(
      options.memberId,
      options.anchorMonth,
      maxMonth
    );
    return months.map((month) => {
      const b = byMonth.get(month)!;
      const displayMonth = shiftOffset > 0 ? shiftMonthKey(month, shiftOffset) : month;
      return {
        month: displayMonth,
        verascore: computeComposite(b),
        factor_dti: b.factor_dti,
        factor_cash_on_hand: b.factor_cash_on_hand,
        factor_spending: b.factor_spending,
        factor_savings: b.factor_savings,
        factor_debt: b.factor_debt,
        factor_payment_history: b.factor_payment_history,
        dti_ratio: b.dti_ratio,
        number_of_late_payments: b.number_of_late_payments,
        gross_income: b.gross_income,
        money_in: b.gross_income,
        monthly_expenses: b.monthly_expenses,
        money_leftover: b.money_leftover,
        net_savings: b.net_savings,
        liquid_reserves: b.liquid_reserves,
        minimum_debt_payments: b.minimum_debt_payments,
        total_outstanding_adverse_debt: b.total_outstanding_adverse_debt,
      } as FinancialHealthStats;
    });
  } catch (err) {
    console.warn('[demo2EvidenceFallback] Failed to build fallback history:', err);
    return [];
  }
}

export function overlayLatestMonth(
  base: FinancialHealthStats[],
  latest: FinancialHealthStats | undefined
): FinancialHealthStats[] {
  if (!latest || !latest.month) return base;
  const out = [...base];
  const idx = out.findIndex((s) => s.month === latest.month);
  if (idx >= 0) {
    out[idx] = { ...out[idx], ...latest };
    return out;
  }
  out.push(latest);
  out.sort((a, b) => a.month.localeCompare(b.month));
  return out;
}
